// Stamping a schedule as ORDINARY FORMULAS.
//
// This replaces the bound "live block": a rectangle the app owned, wrote values
// into, tinted blue, and refused edits to. It was weird in every direction —
// the formula bar could not show a formula because there wasn't one, typing in
// a cell silently did nothing, and the rectangle had a row budget that could
// quietly hide data.
//
// A stamped schedule is just cells. Each one holds a real `GI.*` formula, so it
// recalculates when the project bakes (GI calls are volatile), the formula bar
// shows it because it IS one, and every cell can be edited, copied, deleted or
// referenced like any other. Nothing to own, nothing to refresh, nothing to
// explain.

import type { FormatId, Sheet, SourceKind } from '@/model/types';
import type { AggKind } from '@/data/aggregate';
import type { GiraffeData, MeasureClass } from '@/data/types';
import { prettyField } from '@/data/classify';
import { columnHeader, unitsOf } from '@/data/units';
import { parseFilter } from '@/data/filter';
import { formatForField } from '@/engine/format';
import { rowColToA1, colToLabel } from '@/model/address';
import { setCellFormat, setCellRaw } from '@/model/workbook';
import type { Engine } from '@/engine/engine';

export interface ScheduleMeasure {
  field: string;
  agg: AggKind;
  label?: string;
  fmt?: FormatId;
  /**
   * A per-geometry EXPRESSION instead of a single field, stamped as
   * `GI.SUMEACH(...)`. Cost is `grossArea * hardCost` worked out for every
   * section and added up — not gross area times an average rate, which is a
   * different and usually wrong number.
   */
  expr?: string;
}

export interface ScheduleSpec {
  source: SourceKind;
  /** Field whose distinct values become the rows. Null means no grouping. */
  groupBy: string | null;
  /** Field whose distinct values become the columns — a cross-tab. */
  pivotBy?: string | null;
  measures: ScheduleMeasure[];
  /** Extra `field=value` clauses, ANDed into every formula. */
  filters: string[];
  /** How many group rows to stamp. They fill as the project has groups. */
  rows: number;
  /** How many pivot columns to stamp. Cross-tab only. */
  cols?: number;
  showHeader: boolean;
  showTotal: boolean;
}

export interface StampedRect {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

/** The GI function that performs a given reduction. */
export const GI_FOR_AGG: Record<string, string> = {
  sum: 'GI.TOTAL',
  avg: 'GI.AVG',
  weighted: 'GI.WEIGHTEDAVG',
  min: 'GI.MIN',
  max: 'GI.MAX',
  countUnique: 'GI.COUNTUNIQUE',
  first: 'GI.VALUE',
};

/**
 * `auto` defers to the field's measure class. It is resolved HERE, at stamp
 * time, because the cell has to hold one concrete function — and GI.TOTAL over
 * a rate would be a plausible formula returning the wrong number.
 */
export function resolveAgg(agg: AggKind, cls: MeasureClass): Exclude<AggKind, 'auto'> {
  if (agg !== 'auto') return agg;
  if (cls === 'rate') return 'weighted';
  if (cls === 'text' || cls === 'nested') return 'first';
  return 'sum';
}

/**
 * A clause whose value comes from a cell: `GI.WHERE("usage",$A17)`.
 *
 * This was `"usage="&$A17`, the same thing written as text arithmetic, and
 * nobody should have to work that out from looking at it: a filter IS text, so
 * a value from a cell has to be joined on with `&`, and the quotes around
 * `usage=` are only the quotes any text argument needs. Obvious once you know,
 * and nothing on screen says so.
 *
 * Before that it quoted the value as well, four quote marks in a row, to stop a
 * group named "Retail, ground" splitting the filter in two. GI.WHERE quotes
 * only when it has to, and the filter parser forgives the rest.
 */
export function cellClause(field: string, ref: string): string[] {
  return [JSON.stringify(field), ref];
}

/**
 * Assemble the filter argument.
 *
 * With no cell-driven clause it is a plain string, which is the simplest thing
 * that can be read. With one it becomes a single GI.WHERE call — the FIXED
 * clauses folded in as pairs too, so there is no `&` anywhere and the whole
 * filter is one readable call:
 *
 *   GI.WHERE("levels>",3,"usage",$A2)
 */
export function filterArg(fixed: readonly string[], cellPairs: readonly string[][]): string {
  const literal = fixed.filter((f) => f.trim() !== '').join(', ');
  if (cellPairs.length === 0) return literal === '' ? '""' : JSON.stringify(literal);

  const pairs: string[] = [];
  for (const clause of parseFilter(literal).clauses) {
    // A number stays a number: GI.WHERE("levels>",3) is what someone would
    // have typed, and "3" invites the question of whether it matters.
    const value = clause.value.trim();
    const numeric = value !== '' && Number.isFinite(Number(value));
    pairs.push(JSON.stringify(`${clause.field}${clause.op}`), numeric ? value : JSON.stringify(value));
  }
  for (const pair of cellPairs) pairs.push(...pair);
  return call('GI.WHERE', pairs);
}

/**
 * Omitted only when it matches `GI.*`'s own default — `'areas'` now, not
 * `'bakedSections'` (see engine/fns/gi.ts's `asSource`). A schedule
 * explicitly built against `'bakedSections'`/`'rawSections'`/`'usages'`
 * always gets a literal source argument in its generated formula, so the
 * number it shows does not silently change meaning if that default ever
 * moves again.
 */
export function sourceArg(source: SourceKind): string {
  return source === 'areas' ? '' : JSON.stringify(source);
}

/** Trim trailing empty arguments — `GI.TOTAL("area","")` reads worse than one arg. */
export function call(fn: string, args: string[]): string {
  const list = [...args];
  while (list.length > 0 && (list[list.length - 1] === '' || list[list.length - 1] === '""')) list.pop();
  return `${fn}(${list.join(',')})`;
}

const GI_FOR_EACH: Record<string, string> = {
  sum: 'GI.SUMEACH',
  avg: 'GI.AVGEACH',
  min: 'GI.MINEACH',
  max: 'GI.MAXEACH',
  weighted: 'GI.AVGEACH',
};

export function measureFormula(
  spec: ScheduleSpec,
  measure: ScheduleMeasure,
  cls: MeasureClass,
  cellPairs: readonly string[][]
): string {
  const filter = filterArg(spec.filters, cellPairs);
  const source = sourceArg(spec.source);

  // An expression column loops per geometry; a field column aggregates one
  // field. They are different questions and neither substitutes for the other.
  if (measure.expr) {
    const fn = GI_FOR_EACH[measure.agg === 'auto' ? 'sum' : measure.agg] ?? 'GI.SUMEACH';
    return call(fn, [JSON.stringify(measure.expr), filter, source]);
  }

  const agg = resolveAgg(measure.agg, cls);
  if (agg === 'count') return call('GI.COUNT', [filter, source]);
  const fn = GI_FOR_AGG[agg] ?? 'GI.TOTAL';
  return call(fn, [JSON.stringify(measure.field), filter, source]);
}

/**
 * Write the schedule. Returns the rectangle it covered, purely so the caller can
 * select it — nothing owns the cells afterwards.
 */
export function stampSchedule(
  sheet: Sheet,
  engine: Engine,
  anchor: { row: number; col: number },
  spec: ScheduleSpec,
  data: GiraffeData
): StampedRect {
  return spec.pivotBy
    ? stampPivot(sheet, engine, anchor, spec, data)
    : stampTable(sheet, engine, anchor, spec, data);
}

function stampTable(
  sheet: Sheet,
  engine: Engine,
  anchor: { row: number; col: number },
  spec: ScheduleSpec,
  data: GiraffeData
): StampedRect {
  const { row: r0, col: c0 } = anchor;
  const headerRows = spec.showHeader ? 1 : 0;
  const bodyStart = r0 + headerRows;
  const width = 1 + spec.measures.length;

  if (spec.showHeader) {
    setCellRaw(sheet, r0, c0, spec.groupBy ? prettyField(spec.groupBy) : 'Section');
    spec.measures.forEach((m, i) => {
      // The unit goes in the header, because the cell below it is a bare
      // number: a column of gross areas in ft² and one in m² look identical.
      // A label the user typed is theirs, and is left exactly as typed.
      setCellRaw(sheet, r0, c0 + 1 + i, m.label ?? columnHeader(m.field, unitsOf(data)));
    });
    boldRow(sheet, r0, c0, width);
  }

  for (let i = 0; i < spec.rows; i++) {
    const r = bodyStart + i;
    const labelRef = `$${colToLabel(c0)}${r}`;

    if (spec.groupBy) {
      // GI.LIST(field, n) returns "" past the last group, so a stamped range
      // simply fills as the project gains usages — no rectangle to resize.
      const listFilter = filterArg(spec.filters, []);
      setCellRaw(
        sheet,
        r,
        c0,
        `=${call('GI.LIST', [JSON.stringify(spec.groupBy), rowIndex(c0, bodyStart), listFilter, sourceArg(spec.source)])}`
      );
    }

    spec.measures.forEach((m, j) => {
      const cls = data.classOf(spec.source, m.field);
      const body = measureFormula(spec, m, cls, spec.groupBy ? [cellClause(spec.groupBy, labelRef)] : []);
      // Guard on the label so the empty tail of the range stays blank rather
      // than showing a row of zeroes.
      const formula = spec.groupBy ? `=IF(${labelRef}="","",${body})` : `=${body}`;
      setCellRaw(sheet, r, c0 + 1 + j, formula);
      setCellFormat(sheet, r, c0 + 1 + j, m.fmt ?? formatForField(m.field));
    });

    if (!spec.groupBy) break; // ungrouped: one row, the whole set
  }

  const lastBody = spec.groupBy ? bodyStart + spec.rows - 1 : bodyStart;
  let r2 = lastBody;

  if (spec.showTotal) {
    const totalRow = lastBody + 1;
    setCellRaw(sheet, totalRow, c0, 'Total');
    spec.measures.forEach((m, j) => {
      const cls = data.classOf(spec.source, m.field);
      setCellRaw(sheet, totalRow, c0 + 1 + j, `=${measureFormula(spec, m, cls, [])}`);
      setCellFormat(sheet, totalRow, c0 + 1 + j, m.fmt ?? formatForField(m.field));
    });
    boldRow(sheet, totalRow, c0, width);
    r2 = totalRow;
  }

  engine.onRangeEdited(sheet.id, r0, c0, r2, c0 + width - 1);
  return { r1: r0, c1: c0, r2, c2: c0 + width - 1 };
}

function stampPivot(
  sheet: Sheet,
  engine: Engine,
  anchor: { row: number; col: number },
  spec: ScheduleSpec,
  data: GiraffeData
): StampedRect {
  const { row: r0, col: c0 } = anchor;
  const measure = spec.measures[0];
  const cols = spec.cols ?? 8;
  const bodyStart = r0 + 1; // a pivot always has a header — it names the columns
  if (!measure) return { r1: r0, c1: c0, r2: r0, c2: c0 };

  const cls = data.classOf(spec.source, measure.field);
  setCellRaw(sheet, r0, c0, spec.groupBy ? prettyField(spec.groupBy) : 'Section');

  for (let j = 0; j < cols; j++) {
    const c = c0 + 1 + j;
    setCellRaw(
      sheet,
      r0,
      c,
      `=${call('GI.LIST', [JSON.stringify(spec.pivotBy!), `COLUMN()-COLUMN($${colToLabel(c0)}$${r0})`, filterArg(spec.filters, []), sourceArg(spec.source)])}`
    );
  }
  if (spec.showTotal) setCellRaw(sheet, r0, c0 + 1 + cols, 'Total');
  boldRow(sheet, r0, c0, 1 + cols + (spec.showTotal ? 1 : 0));

  for (let i = 0; i < spec.rows; i++) {
    const r = bodyStart + i;
    const labelRef = `$${colToLabel(c0)}${r}`;
    setCellRaw(
      sheet,
      r,
      c0,
      `=${call('GI.LIST', [JSON.stringify(spec.groupBy ?? ''), rowIndex(c0, bodyStart), filterArg(spec.filters, []), sourceArg(spec.source)])}`
    );

    for (let j = 0; j < cols; j++) {
      const c = c0 + 1 + j;
      const colRef = `${colToLabel(c)}$${r0}`;
      // Two cell-driven clauses in one GI.WHERE, so a body cell is filtered by
      // BOTH its row and its column.
      const body = measureFormula(spec, measure, cls, [
        cellClause(spec.groupBy ?? '', labelRef),
        cellClause(spec.pivotBy!, colRef),
      ]);
      setCellRaw(sheet, r, c, `=IF(OR(${labelRef}="",${colRef}=""),"",${body})`);
      setCellFormat(sheet, r, c, measure.fmt ?? formatForField(measure.field));
    }

    if (spec.showTotal) {
      const c = c0 + 1 + cols;
      const body = measureFormula(spec, measure, cls, [cellClause(spec.groupBy ?? '', labelRef)]);
      setCellRaw(sheet, r, c, `=IF(${labelRef}="","",${body})`);
      setCellFormat(sheet, r, c, measure.fmt ?? formatForField(measure.field));
    }
  }

  const width = 1 + cols + (spec.showTotal ? 1 : 0);
  const r2 = bodyStart + spec.rows - 1;
  engine.onRangeEdited(sheet.id, r0, c0, r2, c0 + width - 1);
  return { r1: r0, c1: c0, r2, c2: c0 + width - 1 };
}

/**
 * A body row's 1-based position in its schedule, as a formula. Measured from a
 * reference to the row above the body, not a literal row number: `ROW()-10`
 * goes on saying "10" after a row is inserted above the schedule, so every
 * row then lists the next group along and the first one drops out. A
 * reference moves with the insert. Only a body starting on row 1 has no row
 * above to point at.
 */
function rowIndex(col: number, bodyStart: number): string {
  return bodyStart > 1 ? `ROW()-ROW($${colToLabel(col)}$${bodyStart - 1})` : 'ROW()';
}

function boldRow(sheet: Sheet, row: number, col: number, width: number): void {
  for (let i = 0; i < width; i++) {
    const key = `${row},${col + i}`;
    sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, bold: true } };
  }
}

export { rowColToA1 };
